import { toast } from "@/components/common/Toast"
import type { TranslationKeys } from "@/hooks/useTranslation/catalogs"
import { moduleTranslate } from "@/utils/moduleTranslate"
import { requestInAppUpgrade } from "@/utils/upgradeEntry"

/**
 * Machine readable limit errors from the backend (api/limits.py, api/trial_status.py):
 *   429 {"detail": {"error_code": "USAGE_LIMIT_EXCEEDED", "resource", "limit", "period", "message"}}
 *   403 {"detail": {"error_code": "TRIAL_EXPIRED" | "TRIAL_STATUS_UNKNOWN", "message"}, "message"}
 *   403 {"detail": {"error_code": "TRIAL_FEATURE_UNAVAILABLE", "message"}}
 * Parsing lives here so axios (interceptor) and fetch (chat streaming) share one mapping.
 */

export type LimitError =
  | {
      kind: "usage_limit"
      resource: string
      limit: number | null
      period: string | null
      /** Credits only: what is left, and what the refused action needed. */
      remaining?: number
      required?: number
    }
  | { kind: "trial_expired" }
  | { kind: "trial_status_unknown" }
  | { kind: "trial_feature_unavailable" }

/** Window event fired on a trial-expiry 403; UpgradeModal listens to it. */
export const TRIAL_EXPIRED_EVENT = "turnus:trial-expired"

type Translate = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

const USAGE_LIMIT_TOAST_ID = "usage-limit"
const TRIAL_EXPIRED_TOAST_ID = "trial-expired"
const TRIAL_STATUS_UNKNOWN_TOAST_ID = "trial-status-unknown"
const TRIAL_FEATURE_UNAVAILABLE_TOAST_ID = "trial-feature-unavailable"

const RESOURCE_LABEL_KEYS: Record<string, TranslationKeys> = {
  questionnaires: "USAGE_TRACKER/QUESTIONNAIRES",
  files: "USAGE_TRACKER/FILES",
  chat_interactions: "USAGE_TRACKER/CHAT_MESSAGES",
  chat_messages_daily: "USAGE_TRACKER/CHAT_DAILY",
  extension_interactions: "USAGE_TRACKER/EXTENSION_CALLS",
  auto_fill_runs: "USAGE_TRACKER/AUTO_FILL_RUNS",
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

/** Maps an HTTP status plus parsed JSON body to a limit error, or null when the
 *  response is not one of the structured limit errors. */
export function parseLimitError(
  status: number | undefined,
  body: unknown,
): LimitError | null {
  if (!isRecord(body) || !isRecord(body.detail)) return null
  const { error_code, resource, limit, period, remaining, required } =
    body.detail

  if (status === 429 && error_code === "USAGE_LIMIT_EXCEEDED") {
    return {
      kind: "usage_limit",
      resource: typeof resource === "string" ? resource : "",
      limit: typeof limit === "number" ? limit : null,
      period: typeof period === "string" ? period : null,
      ...(typeof remaining === "number" && typeof required === "number"
        ? { remaining, required }
        : {}),
    }
  }
  if (status === 403 && error_code === "TRIAL_EXPIRED") {
    return { kind: "trial_expired" }
  }
  if (status === 403 && error_code === "TRIAL_STATUS_UNKNOWN") {
    return { kind: "trial_status_unknown" }
  }
  if (status === 403 && error_code === "TRIAL_FEATURE_UNAVAILABLE") {
    return { kind: "trial_feature_unavailable" }
  }
  return null
}

/** Error for failed fetch calls that keeps the status and parsed body, so
 *  callers can map structured limit errors instead of matching message text. */
export class HttpRequestError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = "HttpRequestError"
    this.status = status
    this.body = body
  }
}

/** Builds an HttpRequestError from a failed fetch response body text. */
export function httpRequestErrorFromText(
  message: string,
  status: number,
  text: string,
): HttpRequestError {
  let body: unknown = text
  try {
    body = JSON.parse(text)
  } catch {
    // not JSON: keep the raw text
  }
  return new HttpRequestError(message, status, body)
}

/** The structured limit error behind a failed call: an HttpRequestError from a
 *  fetch call, or a failed axios request (status and body on `response`). */
export function limitErrorFromException(error: unknown): LimitError | null {
  if (error instanceof HttpRequestError) {
    return parseLimitError(error.status, error.body)
  }
  if (isRecord(error) && isRecord(error.response)) {
    const { status, data } = error.response
    return parseLimitError(
      typeof status === "number" ? status : undefined,
      data,
    )
  }
  return null
}

/** True when the failure is a structured limit error. The axios interceptor
 *  already showed its upgrade toast, so a per-screen error handler checks this
 *  before adding a generic toast of its own (two toasts for one failure). */
export function isLimitError(error: unknown): boolean {
  return limitErrorFromException(error) !== null
}

// Opens /upgrade. The in-app CheckoutStep marks the upgrade pending when it
// creates a checkout session, so the toast must not.
function openUpgradePage(): void {
  requestInAppUpgrade()
}

/** Toast with an Upgrade action for a structured limit error. A trial expiry
 *  additionally asks the UpgradeModal to open (it decides if it was already
 *  shown to this user). An unverifiable trial clock is a token problem, not a
 *  plan limit: it asks for a fresh sign-in and offers no upgrade. */
export function notifyLimitError(
  limitError: LimitError,
  translate: Translate = moduleTranslate,
): void {
  const action = {
    label: translate("FREE_TRIAL/UPGRADE"),
    onClick: openUpgradePage,
  }

  if (limitError.kind === "trial_expired") {
    toast.error(translate("LIMIT_ERROR/TRIAL_EXPIRED"), {
      id: TRIAL_EXPIRED_TOAST_ID,
      action,
    })
    window.dispatchEvent(new CustomEvent(TRIAL_EXPIRED_EVENT))
    return
  }

  if (limitError.kind === "trial_status_unknown") {
    toast.error(translate("LIMIT_ERROR/TRIAL_STATUS_UNKNOWN"), {
      id: TRIAL_STATUS_UNKNOWN_TOAST_ID,
    })
    return
  }

  if (limitError.kind === "trial_feature_unavailable") {
    toast.error(translate("LIMIT_ERROR/TRIAL_FEATURE_UNAVAILABLE"), {
      id: TRIAL_FEATURE_UNAVAILABLE_TOAST_ID,
      action,
    })
    return
  }

  if (limitError.remaining !== undefined) {
    toast.error(
      translate("LIMIT_ERROR/CREDITS", {
        remaining: limitError.remaining,
        required: limitError.required ?? 0,
      }),
      { id: USAGE_LIMIT_TOAST_ID, action },
    )
    return
  }

  const labelKey = RESOURCE_LABEL_KEYS[limitError.resource]
  const message =
    labelKey && limitError.limit !== null
      ? translate("LIMIT_ERROR/USAGE_LIMIT", {
          resource: translate(labelKey),
          limit: limitError.limit,
        })
      : translate("LIMIT_ERROR/USAGE_LIMIT_GENERIC")
  toast.error(message, { id: USAGE_LIMIT_TOAST_ID, action })
}

/** Toast for a failed chat send. Structured limit errors get the resource
 *  specific message with the Upgrade action. A 429 without a structured body
 *  can only be the daily message cap, whose backend detail is a plain string. */
export function notifyChatSendError(
  error: unknown,
  translate: Translate = moduleTranslate,
): void {
  const limitError = limitErrorFromException(error)
  if (limitError) {
    notifyLimitError(limitError, translate)
  } else if (error instanceof HttpRequestError && error.status === 429) {
    toast.error(translate("AI_CHAT/RATE_LIMIT_EXCEEDED"))
  } else {
    toast.error(translate("AI_CHAT/SEND_ERROR"))
  }
}
